// Pure per-frame compositor for the video export. Draws the player's CONTENT
// layers (backdrop → slide/photo+zoom → deck slide-out → doodle → Rive character →
// captions) onto one offscreen canvas, in z-order. It NEVER draws PlayerControls,
// the scrubber, or debug boxes — content is excluded by construction. The geometry
// helpers are pure + unit-tested; drawFrame orchestrates the layers.
import type { AspectRatio, VisibleWord, Mode, ZoomResult } from "../types";
import { containRect } from "../geometry";
import { drawCaptions, type CaptionFont } from "./captionCanvas";
import type { CaptionMask, BleepMode } from "../../config/censorship";

export interface FrameDims { w: number; h: number }

/** Canonical export resolution per aspect (the stage has no intrinsic pixels). */
export function aspectDims(aspect: AspectRatio): FrameDims {
  return aspect === "16:9" ? { w: 1920, h: 1080 } : { w: 1080, h: 1920 };
}

export interface Rect { x: number; y: number; w: number; h: number }

function natOf(src: CanvasImageSource): { w: number; h: number } {
  const a = src as unknown as { naturalWidth?: number; naturalHeight?: number; videoWidth?: number; videoHeight?: number; width?: number; height?: number };
  return {
    w: a.naturalWidth || a.videoWidth || a.width || 0,
    h: a.naturalHeight || a.videoHeight || a.height || 0,
  };
}

/** Parse the player's `translate3d(txpx, typx, 0) scale(s)` into numbers. The tx/ty
 *  must already be in EXPORT pixels (caller computes computeZoom at the export dims). */
export function parseZoomTransform(zoom: ZoomResult): { tx: number; ty: number; scale: number } {
  const m = /translate3d\(\s*(-?[\d.]+)px\s*,\s*(-?[\d.]+)px\s*,\s*0\s*\)\s*scale\(\s*([\d.]+)\s*\)/.exec(zoom.transform);
  if (!m) return { tx: 0, ty: 0, scale: zoom.scale };
  return { tx: parseFloat(m[1]), ty: parseFloat(m[2]), scale: parseFloat(m[3]) };
}

/** object-contain rect of natW×natH inside dims, then the optional center-origin zoom
 *  (scale about the box centre, then translate) — mirrors RoastPlayer.applyImgTransform. */
export function containImageRect(dims: FrameDims, natW: number, natH: number, zoom: ZoomResult | null): Rect {
  const { offX, offY, dispW, dispH } = containRect(dims.w, dims.h, natW, natH);
  if (!zoom) return { x: offX, y: offY, w: dispW, h: dispH };
  const { tx, ty, scale } = parseZoomTransform(zoom);
  const cx = dims.w / 2, cy = dims.h / 2;
  return {
    x: cx + (offX - cx) * scale + tx,
    y: cy + (offY - cy) * scale + ty,
    w: dispW * scale,
    h: dispH * scale,
  };
}

/** object-cover rect with an overscan multiplier (the backdrop uses scale(1.1)). */
export function coverImageRect(dims: FrameDims, natW: number, natH: number, overscan = 1): Rect {
  if (!natW || !natH) return { x: 0, y: 0, w: dims.w, h: dims.h };
  const scale = Math.max(dims.w / natW, dims.h / natH) * overscan;
  const w = natW * scale, h = natH * scale;
  return { x: (dims.w - w) / 2, y: (dims.h - h) / 2, w, h };
}

// CSS `ease` = cubic-bezier(0.25, 0.1, 0.25, 1.0). Map a linear time fraction → eased.
function cubicBezierEase(x1: number, y1: number, x2: number, y2: number) {
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number) => ((ay * t + by) * t + cy) * t;
  const dX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  return (x: number) => {
    let t = x;
    for (let i = 0; i < 6; i++) {
      const xe = sampleX(t) - x;
      const d = dX(t);
      if (Math.abs(xe) < 1e-5 || d === 0) break;
      t -= xe / d;
    }
    return sampleY(Math.max(0, Math.min(1, t)));
  };
}
const EASE = cubicBezierEase(0.25, 0.1, 0.25, 1.0);
/** CSS `ease-in-out` = cubic-bezier(0.42,0,0.58,1) — used to match DOM opacity transitions. */
export const easeInOut = cubicBezierEase(0.42, 0, 0.58, 1.0);

/** Outgoing deck slide translateX: 0 (progress 0) → -w (progress 1), CSS-`ease` timed. */
export function slideOutOffset(progress: number, w: number): number {
  const p = Math.max(0, Math.min(1, progress));
  return -EASE(p) * w;
}

// ---------------------------------------------------------------------------

export interface CaptureFrame {
  dims: FrameDims;
  /** main slide/photo image (drawn object-contain with zoom). */
  bg: CanvasImageSource | null;
  /** pre-rendered blurred+darkened backdrop canvas (object-cover). */
  backdrop: CanvasImageSource | null;
  /** zoom computed at EXPORT dims (null = no zoom). */
  zoom: ZoomResult | null;
  /** deck slide-out overlay: outgoing image + animation progress 0→1. */
  slideOut: { img: CanvasImageSource; progress: number } | null;
  /** doodle reveal overlay + its current fade opacity. */
  doodle: { img: CanvasImageSource; opacity: number } | null;
  /** the live Rive character canvas (fills the stage; same aspect as dims). */
  rive: CanvasImageSource | null;
  captions: { words: VisibleWord[]; mode: Mode; highlightActive: boolean };
  captionFont: CaptionFont;
  /** Caption redaction style; default "vowels" (== today). Per-instance (not per-frame). */
  captionMask?: CaptionMask;
  /** Roast's bleep mode; default "tone". Per-instance. Gates soundalike stand-in redaction. */
  bleepMode?: BleepMode;
}

/** Draw one full frame in content z-order. Pure given inputs; no controls/debug. */
export function drawFrame(ctx: CanvasRenderingContext2D, frame: CaptureFrame): void {
  const { dims } = frame;

  // 0. opaque black base (matches the stage bg-black behind letterbox bars)
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, dims.w, dims.h);

  // 1. blurred backdrop (object-cover, pre-darkened by the session)
  if (frame.backdrop) {
    const n = natOf(frame.backdrop);
    const r = coverImageRect(dims, n.w, n.h, 1.0); // overscan already baked into backdrop canvas
    ctx.drawImage(frame.backdrop, r.x, r.y, r.w, r.h);
  }

  // 2. main slide/photo (object-contain + zoom)
  if (frame.bg) {
    const n = natOf(frame.bg);
    if (n.w && n.h) {
      const r = containImageRect(dims, n.w, n.h, frame.zoom);
      ctx.drawImage(frame.bg, r.x, r.y, r.w, r.h);
    }
  }

  // 3. deck slide-out (outgoing slide sweeping left)
  if (frame.slideOut) {
    const n = natOf(frame.slideOut.img);
    if (n.w && n.h) {
      const r = containImageRect(dims, n.w, n.h, null);
      const dx = slideOutOffset(frame.slideOut.progress, dims.w);
      ctx.drawImage(frame.slideOut.img, r.x + dx, r.y, r.w, r.h);
    }
  }

  // 4. doodle (object-contain, fading in)
  if (frame.doodle && frame.doodle.opacity > 0) {
    const n = natOf(frame.doodle.img);
    if (n.w && n.h) {
      const r = containImageRect(dims, n.w, n.h, null);
      const prev = ctx.globalAlpha;
      ctx.globalAlpha = Math.max(0, Math.min(1, frame.doodle.opacity));
      ctx.drawImage(frame.doodle.img, r.x, r.y, r.w, r.h);
      ctx.globalAlpha = prev;
    }
  }

  // 5. Rive character (fills the stage; identical aspect → no distortion)
  if (frame.rive) {
    ctx.drawImage(frame.rive, 0, 0, dims.w, dims.h);
  }

  // 6. captions (DOM caption layer re-rendered to canvas)
  drawCaptions(ctx, frame.captions.words, {
    mode: frame.captions.mode,
    highlightActive: frame.captions.highlightActive,
    dims,
    font: frame.captionFont,
    captionMask: frame.captionMask,
    bleepMode: frame.bleepMode,
  });
}
